import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Edit2, Save, X, Camera, Trash2, LogOut, ArrowLeft, User, Mail, Phone, Shield, ChevronRight } from 'lucide-react';

const NAVY = '#0f2d6b';
const GOLD = '#e8a020';

const labelCls = 'block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5';
const inputCls = (disabled) =>
  `w-full px-4 py-3 border rounded-xl text-sm transition-all focus:outline-none ${
    disabled
      ? 'bg-slate-50 border-slate-100 text-slate-700 cursor-default'
      : 'bg-white border-slate-200 text-slate-900 focus:ring-2 focus:ring-[#0f2d6b]/20 focus:border-[#0f2d6b]'
  }`;

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing]       = useState(false);
  const [loading, setLoading]           = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [, setProfileImage]             = useState(null);
  const fileInputRef                    = useRef(null);

  const [formData, setFormData] = useState({
    firstName: user?.first_name || user?.user_metadata?.first_name || '',
    lastName:  user?.last_name  || user?.user_metadata?.last_name  || '',
    email:     user?.email || '',
    phone:     user?.phone_number || user?.user_metadata?.phone_number || '',
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      const cachedUser = JSON.parse(localStorage.getItem('ab_user_cache') || '{}');
      localStorage.setItem('ab_user_cache', JSON.stringify({ ...cachedUser, first_name: formData.firstName, last_name: formData.lastName, email: formData.email }));
      setIsEditing(false);
    } catch (e) { console.error('[PROFILE]', e); }
    finally { setLoading(false); }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.first_name || user?.user_metadata?.first_name || '',
      lastName:  user?.last_name  || user?.user_metadata?.last_name  || '',
      email:     user?.email || '',
      phone:     user?.phone_number || user?.user_metadata?.phone_number || '',
    });
    setIsEditing(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: NAVY }} />
      </div>
    );
  }

  const initials = `${formData.firstName[0] || ''}${formData.lastName[0] || ''}`.toUpperCase() || '?';

  return (
    <div className="min-h-screen bg-slate-50 pb-24">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3f8f 100%)` }}>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-6 left-1/4 w-28 h-28 rounded-full pointer-events-none" style={{ background: `${GOLD}12` }} />

        <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 pt-4 pb-12">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => navigate('/dashboard')}
              className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
              <ArrowLeft size={16} className="text-white" />
            </button>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-red-500/30 transition-colors text-white/80 text-xs font-medium">
              <LogOut size={13} />
              Déconnexion
            </button>
          </div>

          {/* Avatar + name */}
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <div className="w-16 h-16 rounded-2xl overflow-hidden ring-2 ring-white/30 flex items-center justify-center text-xl font-extrabold"
                style={{ background: GOLD, color: NAVY }}>
                {previewImage
                  ? <img src={previewImage} alt="avatar" className="w-full h-full object-cover" />
                  : initials}
              </div>
              <button onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center hover:bg-slate-50 transition-colors">
                <Camera size={11} style={{ color: NAVY }} />
              </button>
              {previewImage && (
                <button onClick={() => { setPreviewImage(null); setProfileImage(null); }}
                  className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full shadow flex items-center justify-center hover:bg-red-600 transition-colors">
                  <Trash2 size={10} className="text-white" />
                </button>
              )}
            </div>
            <div>
              <h1 className="text-white font-extrabold text-lg leading-tight">
                {formData.firstName} {formData.lastName}
              </h1>
              <p className="text-white/50 text-xs mt-0.5 capitalize">{user?.role || 'Client'} · AB Campus Finance</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-6 space-y-4">

        {/* Info card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          {/* Card header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <User size={15} style={{ color: NAVY }} />
              <span className="font-bold text-slate-900 text-sm">Informations personnelles</span>
            </div>
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white transition-opacity hover:opacity-90"
                style={{ background: NAVY }}>
                <Edit2 size={12} /> Modifier
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSave} disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white disabled:opacity-50"
                  style={{ background: '#16A34A' }}>
                  <Save size={12} /> {loading ? 'Sauvegarde…' : 'Sauvegarder'}
                </button>
                <button onClick={handleCancel}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200">
                  <X size={12} /> Annuler
                </button>
              </div>
            )}
          </div>

          {/* Fields */}
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Prénom</label>
              <input type="text" value={formData.firstName} disabled={!isEditing}
                onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))}
                className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className={labelCls}>Nom</label>
              <input type="text" value={formData.lastName} disabled={!isEditing}
                onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))}
                className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className={labelCls}><span className="flex items-center gap-1"><Mail size={10} /> Email</span></label>
              <input type="email" value={formData.email} disabled={!isEditing}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className={labelCls}><span className="flex items-center gap-1"><Phone size={10} /> Téléphone</span></label>
              <input type="tel" value={formData.phone} disabled={!isEditing}
                onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                className={inputCls(!isEditing)} />
            </div>
          </div>
        </div>

        {/* Security card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
            <Shield size={15} style={{ color: NAVY }} />
            <span className="font-bold text-slate-900 text-sm">Sécurité</span>
          </div>
          <div className="p-5 space-y-3">
            <button onClick={() => navigate('/forgot-password')}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors group">
              <span className="text-slate-700 text-sm font-medium">Changer le mot de passe</span>
              <ChevronRight size={15} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
            </button>
          </div>
        </div>

        {/* Logout */}
        <button onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-red-100 text-red-600 font-semibold text-sm hover:bg-red-50 transition-colors">
          <LogOut size={16} />
          Se déconnecter
        </button>
      </div>
    </div>
  );
};

export default Profile;
